import { i18nKeys } from '@/src/utils/i18n.utils';
import { Box, Button, SpaceBetween, ToggleButton } from '@amzn/awsui-components-react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { tabbable } from 'tabbable';
import styles from './FeedbackStars.module.scss';

const MAX_RATING = 5;

interface FeedbackStarsProps {
  label: string;
  rating: number;
  setRating: (value: number) => void;
}

const FeedbackStars: React.FC<FeedbackStarsProps> = ({ label, rating, setRating }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLElement>();
  const stars = [];

  for (let i = 1; i <= MAX_RATING; i++) {
    stars.push(
      <ToggleButton
        key={i}
        data-testid={`feedback-star-${i}`}
        variant="icon"
        iconName={i <= rating ? 'star-filled' : 'star'}
        onChange={() => setRating(i)}
        pressed={i <= rating}
        ariaLabel={t(i18nKeys.feedback.labels.starRating, { rating: i, count: MAX_RATING })}
      />
    );
  }

  const clearRating = () => {
    setRating(0);

    if (!containerRef.current) {
      return;
    }

    const elements = tabbable(containerRef.current);

    if (!elements?.length) {
      return;
    }

    elements[0].focus();
  };

  return (
    <div className={styles.container} ref={containerRef as any}>
      <fieldset>
        <legend>
          <Box variant="strong">{label}</Box>
        </legend>

        <span className={styles.srOnly} aria-live="polite">
          {t(i18nKeys.feedback.labels.selectedRating, { rating, count: MAX_RATING })}
        </span>

        <SpaceBetween size="m" direction="horizontal" alignItems="center">
          <Box className="review-stars" margin={{ right: 'm' }}>
            {stars}
          </Box>
          {rating > 0 && (
            <Button variant="inline-link" onClick={() => clearRating()}>
              {t(i18nKeys.myJams.feedback.label.clear)}
            </Button>
          )}
        </SpaceBetween>
      </fieldset>
    </div>
  );
};

export default FeedbackStars;
